import React from 'react';
import { cn } from '../../../lib/utils';

interface ToggleSwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
  description?: string;
  disabled?: boolean;
  className?: string;
  /** Stable id for deterministic test locators (data-testid + aria-label fallback). */
  testId?: string;
}

export const ToggleSwitch: React.FC<ToggleSwitchProps> = ({
  checked,
  onChange,
  label,
  description,
  disabled = false,
  className,
  testId,
}) => {
  // Stable button id so the visible label can target the switch via htmlFor.
  // Falls back to a React-generated id when no testId is provided.
  const reactId = React.useId();
  const buttonId = testId ? `toggle-btn-${testId}` : `toggle-btn-${reactId}`;
  return (
    <div className={cn('flex items-start gap-3', className)}>
      {/* Outer hitbox enforces 44 × 44 minimum on touch devices while the
          visible track stays compact. The visual track is centred inside. */}
      <button
        type="button"
        id={buttonId}
        role="switch"
        aria-checked={checked}
        aria-label={label}
        data-testid={testId ? `toggle-${testId}` : undefined}
        disabled={disabled}
        onClick={() => !disabled && onChange(!checked)}
        className={cn(
          'relative inline-flex flex-shrink-0 items-center justify-center cursor-pointer',
          'min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 -mx-1 md:mx-0 px-1 md:px-0',
          'focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed'
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            'relative inline-flex h-7 md:h-6 w-12 md:w-11 rounded-full transition-colors',
            'focus-visible:ring-2 focus-visible:ring-accent-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg-secondary',
            checked
              ? 'bg-accent-primary'
              : 'bg-surface-container border border-border-subtle'
          )}
        >
          <span
            className={cn(
              'pointer-events-none inline-block h-6 w-6 md:h-5 md:w-5 transform rounded-full bg-white shadow-md transition-transform',
              checked ? 'translate-x-[22px] md:translate-x-[22px]' : 'translate-x-0.5'
            )}
          />
        </span>
      </button>
      <div className="flex flex-col gap-0.5">
        {label !== undefined && (
          <label
            htmlFor={buttonId}
            className="text-sm font-medium text-text-secondary cursor-pointer"
          >
            {label}
          </label>
        )}
        {description !== undefined && (
          <p className="text-xs text-text-muted">{description}</p>
        )}
      </div>
    </div>
  );
};

export default ToggleSwitch;
